import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { insuranceRequestApi } from '../api';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { agentCertificateCreator } from '../redux/reducer';
import { queryClient } from '../utils/queryClientUtil';

import insuranceRequestKeys from './models/insuranceRequests/keys';
import { addInfiniteListsItem, camelCaseApiResponse, getPageData, modifyInfiniteListsItem } from './util';

/* QUERIES
========================================================= */
export const useInsuranceRequestData = (id, options = {}) => useQuery(
  insuranceRequestKeys.insuranceRequest(id),
  () => camelCaseApiResponse(() => insuranceRequestApi.getInsuranceRequest(id)),
  options
);

export const useInsuranceRequestInsured = (id, options = {}) => useQuery(
  insuranceRequestKeys.insuranceRequestInsured(id),
  () => camelCaseApiResponse(() => insuranceRequestApi.getInsuranceRequestInsured(id)),
  options
);

export const useInsuranceRequestAgencies = ({
  insuranceRequestId,
  pageSize = 10,
  options = {}
}) => useInfiniteQuery(
  insuranceRequestKeys.agencies(insuranceRequestId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => insuranceRequestApi.getInsuranceRequestAgencies(
    insuranceRequestId,
    { page: pageParam, pageSize }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useAddAgentToInsured = ({ insuranceRequestId, options = {} }) => {
  const dispatch = useDispatch();

  return useMutation(
    () => camelCaseApiResponse(() => insuranceRequestApi.addAgentToInsured(insuranceRequestId)),
    {
      // Retry transient network blips ("Failed to fetch") before surfacing an error.
      retry: 2,
      onError: () => {
        dispatch(createErrorToast({
          title: "We couldn't connect",
          description: 'Something went wrong starting your submission. Please check your connection and try again.'
        }));
      },
      ...options
    }
  );
};

export const useCreateAgency = ({
  insuranceRequestId,
  options = {}
}) => {
  const dispatch = useDispatch();
  const agenciesKey = insuranceRequestKeys.agencies(insuranceRequestId);
  const tempCompanyId = uuidv4();

  return useMutation(
    data => camelCaseApiResponse(() => insuranceRequestApi.createAgency(insuranceRequestId, data)),
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(agenciesKey);

        // const previousAgencies = queryClient.getQueryData(agenciesKey);
        //
        // const tempAgency = {
        //   agencyId: tempCompanyId,
        //   userId: uuidv4(),
        //   license: updateData.license,
        //   agency: {
        //     name: updateData.name,
        //     address1: updateData.address,
        //     address2: '',
        //     city: updateData.city,
        //     state: updateData.state,
        //     zip: updateData.zip,
        //     country: updateData.country
        //   }
        // };
        //
        // addInfiniteListsItem([agenciesKey], tempAgency);

        dispatch(createToast({ title: 'Creating new company/agency...' }));

        // dispatch(agentCertificateCreator.setAgencyId(tempCompanyId));

        // return { previousAgencies };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your company/agency was not created.'
        }));

        queryClient.setQueryData(agenciesKey, context.previousAgencies);
      },
      onSuccess: async data => {
        // const newAgencyId = data.id;

        await queryClient.invalidateQueries(insuranceRequestKeys.agencies(insuranceRequestId));

        dispatch(createSuccessToast({
          title: 'Created new company/agency!',
          duration: 6000
        }));

        // dispatch(agentCertificateCreator.setAgencyId(newAgencyId));
      },
      ...options
    }
  );
};

export const useUpdateAgent = ({
  insuranceRequestId,
  blueprint,
  options = {}
}) => {
  const dispatch = useDispatch();
  const user = useSelector(blueprint.selectUser);

  return useMutation(
    data => camelCaseApiResponse(() => insuranceRequestApi.updateAgentDetails(insuranceRequestId, data)),
    {
      onMutate: async updateData => {
        const updateUser = {
          ...user,
          userFirstName: updateData.first,
          userLastName: updateData.last,
          userPhone: updateData.phone
        };

        dispatch(blueprint.setUser(updateUser));
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your details were not saved correctly'
        }));
      },
      ...options
    }
  );
};

export const useUpdateAgency = ({
  insuranceRequestId,
  options = {}
}) => {
  const dispatch = useDispatch();
  const agenciesKey = insuranceRequestKeys.agencies(insuranceRequestId);

  return useMutation(
    data => camelCaseApiResponse(() => insuranceRequestApi.updateAgency(insuranceRequestId, data)),
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(agenciesKey);

        const previousAgencies = queryClient.getQueryData(agenciesKey);

        modifyInfiniteListsItem(
          [agenciesKey],
          'agencyId',
          updateData.agencyId,
          r => ({
            ...r,
            license: updateData.license,
            agency: {
              ...r.agency,
              name: updateData.name,
              address1: updateData.address,
              address2: r.agency.address2,
              city: updateData.city,
              state: updateData.state,
              zip: updateData.zip,
              country: updateData.country,
              googlePlaceId: updateData.googlePlaceId
            }
          })
        );

        dispatch(createSuccessToast({
          title: 'Company/Agency Updated!',
          duration: 6000
        }));

        return { previousAgencies };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your company/agency was not updated.'
        }));

        queryClient.setQueryData(agenciesKey, context.previousAgencies);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(insuranceRequestKeys.agencies(insuranceRequestId));
      },
      ...options
    }
  );
};

